/**
 * Update folding (30 Sep 2026): within one request (up to the next message from Julian),
 * only the newest reply with text stands; earlier updates tuck under it as "N updates".
 * Pure, so it is unit-tested (test/reply-fold.test.mjs).
 */
import type { UIMessage } from "./types";

/** A visible "⚙️ Auto-resume" line is the system continuing the same request, not a new one. */
export const isContinuationMarker = (m: UIMessage) => m.role === "user" && typeof m.content === "string" && m.content.startsWith("⚙️ Auto-resume");
/** System lines (compaction, "session ended", idle notes, fork notes): never a request's answer. */
export const isSystemRow = (m: UIMessage) => (m.role as string) === "system" ||
  /^a-(?:compact|died|idle|forkfence)-/.test(m.id) || (m as { kind?: string }).kind === "compact";
export const isUpdateRow = (m: UIMessage) => m.role === "assistant" && (m.meta as { phase?: string } | undefined)?.phase === "update" && !m.isStreaming;
export const isAnswerRow = (m: UIMessage) => m.role === "assistant" && !isSystemRow(m) && (m.content ?? "").length > 0;
export const isRequestBreak = (m: UIMessage) => m.role === "user" && !m.meta?.nativeInput && !isContinuationMarker(m);

export interface Fold { anchor: string | null; folded: number; startId: string | null; firstUpdateId: string | null; current: boolean; newest: boolean }

/**
 * Updates fold (30 Sep 2026): within one request (up to the next message from Julian),
 * only the newest reply with text stands; earlier updates tuck under it as "N updates".
 */
export function foldFor(messages: UIMessage[], id: string): Fold {
  const none: Fold = { anchor: null, folded: 0, startId: null, firstUpdateId: null, current: false, newest: false };
  const at = messages.findIndex((m) => m.id === id);
  if (at < 0) return none;
  let end = isAnswerRow(messages[at]!) ? at : -1;
  let current = true;
  for (let i = at + 1; i < messages.length; i++) {
    const m = messages[i]!;
    if (isRequestBreak(m)) { current = false; break; }
    if (isAnswerRow(m)) end = i;
  }
  if (end < 0) return none;
  let start = at;
  for (let i = at - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (isRequestBreak(m)) break;
    start = i;
  }
  let folded = 0, firstUpdateId: string | null = null;
  for (let i = start; i < end; i++) {
    if (isUpdateRow(messages[i]!)) { folded++; firstUpdateId ??= messages[i]!.id; }
  }
  // Newest meaningful row of the whole chat (system lines after it do not count).
  let newest = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]!;
    if (isSystemRow(m) || m.meta?.nativeInput || (m.role === "assistant" && !(m.content ?? "").length)) continue;
    newest = m.id === messages[end]!.id;
    break;
  }
  return { anchor: messages[end]!.id, folded, startId: messages[start]!.id, firstUpdateId, current, newest };
}
